import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Download, ArrowRight, SendHorizontal } from "lucide-react";
import html2pdf from "html2pdf.js";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { PiBuildingApartmentFill } from "react-icons/pi";
import CustomModal from "./CustomModal";
import { parseLocalDate } from "../utils/parseLocalDate";
import { TAX_RATE } from "../config/invoiceConfig";

function NativeCheckbox({ checked, onChange }) {
  return (
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded-sm border border-[#2F7F571A] accent-[#2F7F57]"
    />
  );
}

function money(n) {
  const v = Number.isFinite(n) ? n : 0;
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function toDecimalHours(hours, minutes) {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  return h + m / 60;
}

function formatHHMM(hours, minutes) {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  const safeM = Math.max(0, Math.min(59, m));
  return `${h}:${String(safeM).padStart(2, "0")}`;
}

const safeToast = {
  success: (m) => {
    try {
      toast.success(m);
    } catch (e) {
      console.log(m);
    }
  },
};

function isValidEmail(s) {
  const v = String(s || "").trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function Step3PreviewGenerate({
  property,
  invoiceNumber,
  invoiceDate,
  startDate,
  endDate,
  dueDate,
  rows,
  downloadPdf,
  setDownloadPdf,
  emailClient,
  setEmailClient,
  clientEmail,
  setClientEmail,
  notes,
  errors,
  onValidate,
  onGenerate,
  onEmailTouched,
  pdfFileName,
  autoGeneratePdf,
  onPdfGenerated,
  refPdfGenerate,
  isPreviewMode,
}) {
  const onCheckboxChange = (setter) => (v) => setter(v === true);

  const [generating, setGenerating] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const liveEmailError = useMemo(() => {
    if (!emailClient) return "";
    const v = String(clientEmail || "").trim();
    if (!v) return "Client email is required";
    if (!isValidEmail(v)) return "Please enter a valid email";
    return "";
  }, [clientEmail, emailClient]);

  const lineItems = useMemo(() => {
    return (rows || []).filter((r) => !r?.isNew);
  }, [rows]);

  const totals = useMemo(() => {
    const parsed = lineItems.map((r) => {
      const h = toDecimalHours(r.hours, r.minutes);
      const rate = parseFloat(r.rate) || 0;
      return { h, rate, amt: h * rate };
    });
    const subtotal = parsed.reduce((a, b) => a + b.amt, 0);
    const taxRate = TAX_RATE;
    const tax = subtotal * taxRate;
    const total = subtotal + tax;
    return { subtotal, tax, total, taxRate };
  }, [lineItems]);

  let computedDueDate = null;
  if (invoiceDate) {
    const base =
      invoiceDate instanceof Date ? invoiceDate : parseLocalDate(invoiceDate);
    if (base) {
      const normalized = new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
      );
      normalized.setDate(normalized.getDate() + 14);
      computedDueDate = normalized;
    }
  }
  const previewDueDate = dueDate || computedDueDate;

  const previewRef = useRef(null);

  const generatePdfFromPreview = async () => {
    const container = previewRef.current;
    if (!container || generating) return;

    setGenerating(true);
    const toastId = toast.loading("Generating PDF document...");
    let printHost = null;

    try {
      const contentToPrint = container.cloneNode(true);

      const style = document.createElement("style");
      style.textContent = `
        .invoice-preview-root { background: white; }
        .invoice-preview-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .invoice-preview-rows { page-break-inside: auto; }
        .invoice-preview-row { break-inside: avoid; page-break-inside: avoid; }
        .invoice-preview-summary { break-inside: avoid; page-break-inside: avoid; }
        .invoice-preview-notes { break-inside: avoid; page-break-inside: avoid; }
      `;
      contentToPrint.prepend(style);

      printHost = document.createElement("div");
      printHost.style.position = "fixed";
      printHost.style.left = "0";
      printHost.style.top = "0";
      printHost.style.width = "794px";
      printHost.style.background = "white";
      printHost.style.opacity = "0";
      printHost.style.pointerEvents = "none";
      printHost.style.zIndex = "-1";
      printHost.appendChild(contentToPrint);
      document.body.appendChild(printHost);

      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => setTimeout(r, 100));

      const filenameBase =
        (pdfFileName && String(pdfFileName).trim()) ||
        `invoice-${
          String(invoiceNumber || "")
            .replace("#", "")
            .trim() || "draft"
        }.pdf`;

      const blob = await html2pdf()
        .from(contentToPrint)
        .set({
          margin: 10,
          filename: filenameBase,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 3,
            useCORS: true,
            backgroundColor: "#ffffff",
            windowWidth: 1200,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .outputPdf("blob");

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = filenameBase;
      link.click();

      toast.dismiss(toastId);
      safeToast.success("Downloaded successfully");
      setIsSuccessModalOpen(true);

      URL.revokeObjectURL(url);
      if (typeof onPdfGenerated === "function") onPdfGenerated();
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to generate PDF. Please try again.");
      console.error("PDF Generation error:", error);
    } finally {
      setGenerating(false);
      try {
        printHost?.remove?.();
      } catch (e) {}
    }
  };

  useEffect(() => {
    if (typeof refPdfGenerate === "function") {
      refPdfGenerate(generatePdfFromPreview);
    }
  }, [refPdfGenerate]);

  useEffect(() => {
    if (!autoGeneratePdf) return;
    if (generating) return;
    setGenerating(true);
    Promise.resolve(generatePdfFromPreview())
      .catch(() => {})
      .finally(() => setGenerating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGeneratePdf]);

  return (
    <div className="space-y-4">
      {/* {!isPreviewMode ? (
        <Card className="border-[#2F7F571A]">
          <CardHeader>
            <CardTitle className="text-[#131614]">
              Step 3: Preview & Generate
            </CardTitle>
            <div className="text-sm text-[#6B7280] -mt-2">
              Review the invoice document before final generation.
            </div>
          </CardHeader>
        </Card>
      ) : null} */}

      <div className="w-full  gap-6">
        <Card className="col-span-12 lg:col-span-8 border-[#2F7F571A] shadow-[0px_1px_2px_0px_#0000000D] overflow-hidden">
          <CardContent className="p-0">
            <div className="p-4 sm:p-8 invoice-preview-root" ref={previewRef}>
              <div className="flex flex-col md:flex-row gap-6 items-start justify-between">
                <div className="flex flex-col items-start gap-4">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <div className="h-[2rem] w-[2rem] rounded-md bg-[#2F7F57] flex items-center justify-center text-white">
                      <PiBuildingApartmentFill className=" h-5 w-5" />
                    </div>
                    <div className="text-xl font-black text-[#131614]">
                      Surplus PC INC
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-[#6B7280]">
                      123 Tech Avenue, Suite 400
                    </div>
                    <div className="text-sm text-[#6B7280]">
                      Silicon Valley, CA 94043
                    </div>
                  </div>
                </div>

                <div className="md:text-right text-left">
                  <div className="text-3xl font-extrabold text-[#0F172A]">
                    INVOICE
                  </div>

                  <div className="text-sm mt-3 font-bold text-[#131614]">
                    #
                    {String(invoiceNumber || "")
                      .replace("#", "")
                      .trim() || "—"}
                  </div>
                  <div className=" text-xs text-[#6B7280]">
                    {invoiceDate ? format(invoiceDate, "MMMM dd, yyyy") : "—"}
                  </div>
                </div>
              </div>

              <p className="text-[10px] mt-8 font-bold tracking-widest text-[#131614] mb-2 uppercase">
                Bill to
              </p>

              <div className="grid md:grid-cols-2 grid-cols-1 gap-12 ">
                <div>
                  <div className="bg-[#2F7F570D] p-5 rounded-xl border border-[#2F7F571A] flex flex-col justify-center">
                    <p className="text-xl font-bold text-[#1e293b] leading-tight m-0">
                      {property || "—"}
                    </p>
                    <div className="text-sm text-[#475569] mt-1 space-y-0.5">
                      {/* <p className="m-0">456 Skyline Drive</p>
                      <p className="m-0">Los Angeles, CA 90012</p> */}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-start pb-1">
                  <div className="py-4 border-b border-[#F1F5F9] flex justify-between items-center">
                    <span className="text-sm font-bold tracking-widest text-[#94A3B8] uppercase">
                      Pay Period
                    </span>
                    <span className="text-sm font-semibold text-[#334155]">
                      {startDate && endDate
                        ? `${format(startDate, "MMM dd, yyyy")} - ${format(
                            endDate,
                            "MMM dd, yyyy",
                          )}`
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              {/* <div className="h-px bg-[#2F7F571A] my-8" /> */}
              <p className="uppercase text-[#131614] text-sm font-bold tracking-wider pt-10">
                Employee Payroll Services
              </p>
              <div className="mt-8 rounded-xl  overflow-hidden">
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[620px]">
                    <div className="grid grid-cols-12 text-xs font-bold text-[#131614] tracking-wider uppercase border-b border-[#2F7F571A]">
                      <div className="col-span-1 p-3">ID</div>
                      <div className="col-span-6 p-3">Name</div>
                      <div className="col-span-2 p-3 text-right">Hours</div>
                      <div className="col-span-1 p-3 text-right">Rate</div>
                      <div className="col-span-2 p-3 text-right">Amount</div>
                    </div>

                    {/* <div className="h-px bg-black" /> */}

                    <div className="invoice-preview-rows">
                      {lineItems.map((r, idx) => {
                        const decHours = toDecimalHours(r.hours, r.minutes);
                        const rate = parseFloat(r.rate) || 0;
                        const amt = decHours * rate;
                        return (
                          <React.Fragment key={r.id}>
                            <div className="grid grid-cols-12 invoice-preview-row">
                              <div className="col-span-1 p-3 text-sm text-[#131614]">
                                {r.employeeId
                                  ? String(r.employeeId)
                                  : String(idx + 1).padStart(2, "0")}
                              </div>
                              <div className="col-span-6 p-3">
                                <div className="text-sm font-bold text-[#131614]">
                                  {r.name || "—"}
                                </div>
                                {r.role ? (
                                  <div className="text-xs text-[#9CA3AF] mt-1">
                                    {r.role}
                                  </div>
                                ) : null}
                              </div>
                              <div className="col-span-2 p-3 text-sm text-[#475569] text-right">
                                {formatHHMM(r.hours, r.minutes)}
                              </div>
                              <div className="col-span-1 p-3 text-sm text-[#475569] text-right">
                                {money(rate)}
                              </div>
                              <div className="col-span-2 p-3 text-sm font-bold text-[#0F172A] text-right">
                                {money(amt)}
                              </div>
                            </div>
                            {idx !== lineItems.length - 1 ? (
                              <div className="h-px bg-[#F8FAFC]" />
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-0.5 bg-[#F1F5F9] mt-8" />

              <div className="mt-4 grid grid-cols-12 gap-8 invoice-preview-summary">
                {/* Left: Internal Notes */}
                <div className="col-span-7">
                  {notes && String(notes).trim() ? (
                    <div className="invoice-preview-notes">
                      <div className="text-[10px] font-bold tracking-widest text-[#9CA3AF] uppercase mb-1">
                        INTERNAL NOTES
                      </div>
                      <div className="text-[11px] text-[#64748b] leading-relaxed whitespace-pre-wrap">
                        {notes}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Right: Totals */}
                <div className="col-span-5 flex flex-col items-end">
                  <div className="w-full max-w-[350px]">
                    <div className="flex items-center justify-between text-sm text-[#475569]">
                      <span>Subtotal:</span>
                      <span className="font-bold text-[#1e293b]">
                        {money(totals.subtotal)}
                      </span>
                    </div>
                    <div className="h-px bg-[#e2e8f0] my-2" />
                    <div className="flex items-center justify-between">
                      <span className="text-base font-black text-[#0f172a]">
                        Grand Total:
                      </span>
                      <span className="text-xl font-black text-[#2F7F57]">
                        {money(totals.subtotal)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Success Modal */}
      <CustomModal
        open={isSuccessModalOpen}
        onOpenChange={setIsSuccessModalOpen}
      >
        <div className="p-8">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded-full bg-[#2F7F571A] flex items-center justify-center">
              <div className="h-7 w-7 rounded-full bg-[#2F7F57] flex items-center justify-center text-white text-sm font-black">
                ✓
              </div>
            </div>
          </div>

          <div className="mt-4 text-center text-xl font-black text-[#131614]">
            Invoice Downloaded
          </div>
          <div className="mt-2 text-center text-sm text-[#6B7280]">
            The invoice for {property || "—"} has been successfully generated
            and downloaded to your device.
          </div>

          <div className="mt-6 space-y-3">
            <Button
              className="w-full"
              onClick={() => {
                window.location.href = "/invoices";
              }}
            >
              Go to Invoice List
            </Button>
            <Button
              variant="outline"
              className="w-full text-[#6B7280] hover:text-[#131614]"
              onClick={() => setIsSuccessModalOpen(false)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
